import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'
import ClickOutside from 'vue-click-outside'

import { Body2, Caption } from '@src/elements/typography/typography'

import { DatePicker } from '@src/elements/input/date-picker'
import { Input } from '@src/elements/input/input'
import { Button } from '@src/elements/button/button'

import Member, { MemberState } from '@src/data/member'

import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { noop } from 'vue-class-component/lib/util'
import { stopPropagation } from '@src/util/functions'
import { TrainingPlanService } from '@src/service/training-plan'

import './assign-to-member.sass'

type MembersResponse = { members: Member[] }

@Component({
  directives: {
    ClickOutside
  }
})
export default class AssignToMember extends Vue {

  @Prop({ required: false, default: false })
  hideDate: boolean

  @Prop({ required: false })
  totalDays: number

  @Prop({ required: false })
  callback: (member?: Member | null, startDate?: string) => void

  @Prop({ required: false, default: noop })
  closeCallback: () => void

  @Prop({ required: false, default: null })
  initialSelectedMember: Member | null

  @Prop({ required: false, default: '' })
  initialStartDate: string

  availableMembers: Member[] = []
  searchedText: string = ''

  selectedMember: Member | null
  startDate: string = ''
  endDate: string = ''
  emptyStartDate: boolean = false
  emptyEndDate: boolean = false

  protected membersApiClient: ApiClient

  protected showResults: boolean = true

  constructor () {
    super()
    this.membersApiClient = new ApiClient(API.TRAINERS_API.MEMBERS)
  }

  search (value) {
    this.searchedText = value
    this.selectedMember = null

    if (value.trim() === '') {
      this.callback(null)
    }
  }

  selectMember (member) {
    this.searchedText = member.fullName
    this.selectedMember = member
  }

  computeStartDate () {
    this.startDate = TrainingPlanService.computeStartDate(this.endDate, this.totalDays)
    this.checkDates()
  }

  computeEndDate () {
    this.endDate = TrainingPlanService.computeEndDate(this.startDate, this.totalDays)
    this.checkDates()
  }

  checkDates () {
    this.emptyStartDate = this.startDate === 'Invalid Date' || !this.startDate
    this.emptyEndDate = this.endDate === 'Invalid Date' || !this.endDate
  }

  assignToMember () {
    if (this.selectedMember) {
      if (this.startDate) {
        this.callback(this.selectedMember, this.startDate)
      } else {
        this.callback(this.selectedMember)
      }
    }
  }

  showAssignMemberButton () {
    if (!this.initialSelectedMember) {
      return this.showAssignMemberButtonNoInitialMember()
    } else {
      if (!this.initialStartDate) {
        return this.showAssignMemberButtonInitialMemberNoInitialDates()
      } else {
        return this.showAssignMemberButtonInitialMemberInitialDates()
      }
    }
  }

  showAssignMemberButtonNoInitialMember () {
    return (this.selectedMember && this.startDate !== '' && this.endDate !== '')
      || (this.selectedMember && this.hideDate)
  }

  showAssignMemberButtonInitialMemberNoInitialDates () {
    return (this.selectedMember && this.startDate !== '' && this.endDate !== ''
      && this.initialSelectedMember !== this.selectedMember) || (this.selectedMember && this.hideDate && this.initialSelectedMember !== this.selectedMember)
  }

  showAssignMemberButtonInitialMemberInitialDates () {
    return this.selectedMember && this.startDate !== '' && this.endDate !== ''
      && (this.initialSelectedMember !== this.selectedMember ||
        this.initialSelectedMember === this.selectedMember && this.initialStartDate !== this.startDate)
  }

  onClickOutside () {
    if (this.closeCallback) {
      this.closeCallback()
    }
  }

  render (h: CreateElement) {
    let foundMembers: any[] = []
    let showAssignMemberButton = this.showAssignMemberButton()

    if (this.searchedText) {
      foundMembers = this.availableMembers.filter(member => member.fullName.toLowerCase().indexOf(this.searchedText.toLowerCase().trim()) >= 0).map(member => {
        let highlightedText = member.fullName.replace(new RegExp(this.searchedText, 'gi'), match => {
          return '<span class="highlight">' + match + '</span>'
        })
        return <div onClick={() => this.selectMember(member)}>
          <Body2 domPropsInnerHTML={highlightedText} />
          <Caption class='username'>{member.username}</Caption>
        </div>
      })
    }

    return <div class='assign-to-member-wrapper' v-click-outside={this.onClickOutside}>
      <div onClick={stopPropagation}>
        <div class='search-member'>
          <Input search={true} placeholder={this.$t('PAGE.MEMBERS.SEARCH.PLACEHOLDER')}
                 valueChanged={(value: string) => this.search(value)} value={this.searchedText} />
          {!this.selectedMember && this.showResults && foundMembers.length > 0 ? <div class='search-member-results'>{foundMembers}</div> : null}
        </div>

        {this.selectedMember && !this.hideDate ?
          <div class='date-pickers' style={{ marginTop: '10px' }}>
            <DatePicker allowPastDate={false} class={this.emptyStartDate ? 'error' : ''} type='date' value={this.startDate}
                        appendToBody={false}
                        popper-class={ 'over-modal' }
                        change={(value: string) => {
                          this.startDate = value.toString()
                          this.computeEndDate()
                        }} icon='util/calendar' label={`${this.$t('START_DATE')}:`} />
            <DatePicker allowPastDate={false} class={this.emptyEndDate ? 'error' : ''} type='date' value={this.endDate}
                        appendToBody={false}
                        popper-class={ 'over-modal' }
                        change={(value: string) => {
                          this.endDate = value.toString()
                          this.computeStartDate()
                        }} icon='util/calendar' label={`${this.$t('END_DATE')}:`} />
          </div> : null}
      </div>
      {showAssignMemberButton ?
        <Button type='remove' class='save-btn' label={this.$t('ASSIGN')} click={this.assignToMember} /> : null}
    </div>
  }

  beforeMount () {
    if (this.initialSelectedMember) {
      this.selectedMember = this.initialSelectedMember
      this.searchedText = this.initialSelectedMember.fullName
    }
    if (this.initialStartDate) {
      this.startDate = this.initialStartDate
      this.computeEndDate()
    }
    return this.loadMembers()
  }

  protected loadMembers () {
    return this.membersApiClient.get<MembersResponse>()
      .then((response) => {
        this.availableMembers = response.members.map((attrs) => new Member(camelcaseKeys(attrs)))
          .filter(member => member.state === MemberState.PUBLISHED)
      })
      .catch((error) => console.error('failed to retrieve members', error))
  }
}
